/**
 * Channel Monitor — the ops view of every message and call, INCLUDING the ones
 * SpectraIQ refused.
 *
 * Why this screen exists: when Twilio reports "Error 11200 — HTTP retrieval
 * failure", you cannot tell from inside the product whether the message reached
 * SpectraIQ and was rejected, or never arrived at all. Those have completely
 * different fixes and look identical: silence. Leads/Interactions only show what
 * succeeded, so the failures that most need investigating are exactly the ones
 * that leave no trace there.
 *
 * So this feed shows the *attempt* and its outcome. A rejected signature, a
 * message on a WhatsApp provider you've switched away from — they all appear
 * here, with the reason and what to do about it.
 *
 * Retention: newest 1000 events per dealership, pruned server-side. This is
 * diagnostics; the permanent record lives on the lead.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDate, formatDateTime } from '../lib/datetime';
import TopBar from '../components/TopBar';
import { channelsService } from '../lib/services';

/* ─────────────────────────── presentation ─────────────────────────── */

const CHANNELS   = ['ALL', 'WHATSAPP', 'SMS', 'VOICE'];
const DIRECTIONS = ['ALL', 'INBOUND', 'OUTBOUND'];

/**
 * How each outcome should read, and — for failures — what to actually do.
 * The `fix` text is the difference between a log and a tool.
 */
const STATUS_META = {
  RECEIVED:     { label: 'Received',     tone: 'success' },
  AUTO_REPLIED: { label: 'AI replied',   tone: 'success' },
  SENT:         { label: 'Sent',         tone: 'success' },
  FAILED:       { label: 'Send failed',  tone: 'danger'  },

  REJECTED_SIGNATURE: {
    label: 'Rejected — bad signature',
    tone: 'danger',
    fix: 'SpectraIQ received this and refused it: Twilio’s signature didn’t match any URL '
       + 'we could construct for the request. Twilio reports our 403 back to you as '
       + '“11200 HTTP retrieval failure”, which sounds like a network fault and isn’t. '
       + 'The full breakdown below shows every header we received, every URL we tried, '
       + 'and which credentials we signed with.',
  },
  DROPPED_PROVIDER_INACTIVE: {
    label: 'Dropped — wrong provider',
    tone: 'warning',
    fix: 'This arrived on a WhatsApp provider your dealership isn’t using. There’s no '
       + 'sender to reply from, so it was dropped. Either switch WhatsApp to that '
       + 'provider in Settings → Channels, or remove the webhook from it in Twilio.',
  },
  DROPPED_NO_TENANT: {
    label: 'Dropped — unknown number',
    tone: 'warning',
    fix: 'No dealership owns the number this arrived on. Sync your numbers in '
       + 'Settings → Channels, or select the sender there.',
  },

  // ---- Live voice-call progress (reported by voice-loop during the call) ----
  BRIDGED_AI_AGENT: { label: 'Bridged to AI', tone: 'accent' },
  VOICE_CONNECTED:  { label: 'Voice connected', tone: 'accent' },
  READY:            { label: 'Pipeline ready', tone: 'accent' },
  VOICE_RECEIVED:   { label: 'Caller audio received', tone: 'success' },
  STT_TRANSCRIPT:   { label: 'STT transcribed', tone: 'success' },
  AI_REPLIED:       { label: 'AI replied', tone: 'success' },
  CALL_ENDED:       { label: 'Call ended', tone: '' },
  STT_NO_CREDENTIALS: {
    label: 'STT not configured',
    tone: 'danger',
    fix: 'The caller was heard but their speech was NOT transcribed, so the AI received '
       + 'nothing and could only re-prompt. Set the speech-to-text provider’s API key in '
       + 'Settings → Voice agents (for Deepgram, the same key handles STT and TTS).',
  },
  STT_CONNECT_FAILED: {
    label: 'STT connect failed',
    tone: 'danger',
    fix: 'voice-loop could not open a connection to the speech-to-text provider, so the '
       + 'call produced no transcript. Check the STT API key and that the provider is '
       + 'reachable from voice-loop (Settings → Voice agents).',
  },
};

function statusMeta(status) {
  return STATUS_META[status] || { label: status, tone: '' };
}

function Pill({ tone, children }) {
  return <span className={`pill ${tone} ${tone ? 'filled' : ''}`}>{children}</span>;
}

function timeAgo(iso) {
  if (!iso) return '';
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60)   return `${Math.floor(secs)}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return formatDate(iso);
}

/* ─────────────────────────── row ─────────────────────────── */

function EventRow({ e }) {
  const [open, setOpen] = useState(false);
  const meta = statusMeta(e.status);
  const inbound = e.direction === 'INBOUND';
  const expandable = Boolean(e.detail || meta.fix || e.preview);

  return (
    <>
      <div
        className="flex-row"
        style={{
          padding: '10px 0',
          borderBottom: '1px solid var(--border-subtle)',
          alignItems: 'center',
          gap: 10,
          cursor: expandable ? 'pointer' : 'default',
          // Problems get a left accent — you should be able to scan for them.
          borderLeft: e.problem ? '2px solid var(--danger)' : '2px solid transparent',
          paddingLeft: 8,
        }}
        onClick={() => expandable && setOpen((v) => !v)}
      >
        <span className="mono text-xs text-muted" style={{ width: 56, flexShrink: 0 }}>
          {inbound ? '← in' : '→ out'}
        </span>

        <span className="pill" style={{ width: 78, flexShrink: 0, justifyContent: 'center' }}>
          {e.channel}
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="mono" style={{ fontSize: 12 }}>
            {e.counterparty || '—'}
            <span className="text-muted" style={{ margin: '0 6px' }}>
              {inbound ? '→' : '←'}
            </span>
            <span className="text-muted">{e.ourNumber || '—'}</span>
          </div>
          {e.preview && (
            <div className="text-xs text-muted" style={{
              marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {e.preview}
            </div>
          )}
        </div>

        <Pill tone={meta.tone}>{meta.label}</Pill>

        <span className="mono text-xs text-muted" style={{ width: 72, textAlign: 'right', flexShrink: 0 }}>
          {timeAgo(e.at)}
        </span>
      </div>

      {open && (
        <div style={{
          padding: '10px 12px 14px 66px',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'var(--bg-glass)',
        }}>
          {meta.fix && (
            <div className="text-sm" style={{ lineHeight: 1.65, marginBottom: 8 }}>
              {meta.fix}
            </div>
          )}
          {/* The backend's forensic dump. Multi-line and column-aligned, so it must be
              rendered preformatted — collapsing the whitespace would destroy the very
              alignment that makes "which of these URLs is wrong?" answerable at a glance. */}
          {e.detail && (
            <div style={{ marginBottom: 8 }}>
              <div className="flex-between" style={{ marginBottom: 4 }}>
                <span className="card-label" style={{ marginBottom: 0 }}>DIAGNOSTICS</span>
                <button
                  className="btn small"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    navigator.clipboard?.writeText(e.detail);
                  }}
                >
                  Copy
                </button>
              </div>
              <pre className="mono" style={{
                fontSize: 11,
                lineHeight: 1.6,
                margin: 0,
                padding: 10,
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-secondary)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 420,
                overflow: 'auto',
              }}>{e.detail}</pre>
            </div>
          )}
          <div className="mono text-xs text-muted" style={{ lineHeight: 1.8 }}>
            <div>Provider · {e.provider || '—'}</div>
            {/* The Twilio sid, so you can paste it straight into Twilio's console
                and line this row up with what they logged. */}
            <div>Twilio SID · {e.externalId || '—'}</div>
            <div>When · {formatDateTime(e.at)}</div>
          </div>
        </div>
      )}
    </>
  );
}

/* ─────────────────────────── page ─────────────────────────── */

/**
 * The feed itself, embeddable — rendered inside Settings → Channel monitor
 * (pages/Settings.jsx) as well as by the standalone route below.
 */
export function ChannelMonitorFeed() {
  const [data, setData]           = useState(null);
  const [channel, setChannel]     = useState('ALL');
  const [direction, setDirection] = useState('ALL');
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [live, setLive]           = useState(true);
  const [error, setError]         = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await channelsService.events({ limit: 200, channel, direction });
      setData(r);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    }
  }, [channel, direction]);

  // Polling, matching the portal's convention. 5s here rather than the usual 15 —
  // this screen is used while someone is actively sending a test message and
  // waiting to see whether it landed.
  useEffect(() => {
    load();
    if (!live) return undefined;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load, live]);

  const events = useMemo(() => {
    const all = data?.events || [];
    return problemsOnly ? all.filter((e) => e.problem) : all;
  }, [data, problemsOnly]);

  const problems = data?.problems ?? 0;

  return (
    <div>

        {/* Header + counters */}
        <div className="flex-between" style={{ marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Channel Monitor</div>
            <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>
              Every message and call — including the ones SpectraIQ refused. Newest{' '}
              {data?.cap ?? 1000} events.
            </div>
          </div>
          <div className="flex-row" style={{ gap: 8, alignItems: 'center' }}>
            <label className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
              <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} />
              Live
            </label>
            <button className="btn small" onClick={load}>Refresh</button>
          </div>
        </div>

        {/* Problem banner — the thing you came here for */}
        {problems > 0 && (
          <div className="card" style={{
            borderLeft: '3px solid var(--danger)',
            background: 'var(--danger-bg)',
            padding: '10px 14px',
            marginBottom: 14,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--danger)' }}>
              {problems} event{problems === 1 ? '' : 's'} were received but not processed
            </div>
            <div className="text-sm" style={{ marginTop: 4, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
              These reached SpectraIQ and were refused — which is what Twilio reports back
              to you as “11200 HTTP retrieval failure”. Click a row to see why and how to fix it.
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="card" style={{ padding: '10px 14px', marginBottom: 14 }}>
          <div className="flex-row" style={{ gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="flex-row" style={{ gap: 4, alignItems: 'center' }}>
              <span className="text-xs text-muted" style={{ marginRight: 4 }}>Channel</span>
              {CHANNELS.map((c) => (
                <button
                  key={c}
                  className={`pill ${channel === c ? 'accent filled' : ''}`}
                  style={{ cursor: 'pointer', border: 'none' }}
                  onClick={() => setChannel(c)}
                >
                  {c === 'ALL' ? 'All' : c}
                </button>
              ))}
            </div>

            <div className="flex-row" style={{ gap: 4, alignItems: 'center' }}>
              <span className="text-xs text-muted" style={{ marginRight: 4 }}>Direction</span>
              {DIRECTIONS.map((d) => (
                <button
                  key={d}
                  className={`pill ${direction === d ? 'accent filled' : ''}`}
                  style={{ cursor: 'pointer', border: 'none' }}
                  onClick={() => setDirection(d)}
                >
                  {d === 'ALL' ? 'All' : d === 'INBOUND' ? 'In' : 'Out'}
                </button>
              ))}
            </div>

            <label className="text-xs" style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
              <input type="checkbox" checked={problemsOnly}
                     onChange={(e) => setProblemsOnly(e.target.checked)} />
              Problems only
            </label>

            <span className="mono text-xs text-muted" style={{ marginLeft: 'auto' }}>
              {events.length} shown · {data?.total ?? 0} stored
            </span>
          </div>
        </div>

        {/* Feed */}
        <div className="card animate-in">
          {error && (
            <div className="mono text-xs text-danger" style={{ padding: '10px 0' }}>{error}</div>
          )}

          {!error && events.length === 0 && (
            <div style={{ padding: '28px 0', textAlign: 'center' }}>
              <div className="text-sm text-muted" style={{ marginBottom: 6 }}>
                {problemsOnly ? 'No problems recorded.' : 'Nothing recorded yet.'}
              </div>
              <div className="mono text-xs text-muted" style={{ lineHeight: 1.7 }}>
                {problemsOnly
                  ? 'Everything that reached SpectraIQ was processed.'
                  : 'Send a message to one of your numbers — it should appear here within seconds. '
                    + 'If it doesn’t appear at all, Twilio never reached SpectraIQ: check the '
                    + 'webhook URL on the number, and Twilio’s Monitor → Logs → Errors.'}
              </div>
            </div>
          )}

          {events.map((e) => <EventRow key={e.id} e={e} />)}
        </div>

        <div className="mono text-xs text-muted" style={{ marginTop: 12, lineHeight: 1.7 }}>
          Only the newest {data?.cap ?? 1000} events are kept — this is a diagnostic buffer,
          not the audit trail. The permanent record of a conversation lives on the lead.
        </div>
    </div>
  );
}

/**
 * Standalone route (/channel-monitor) — kept so old bookmarks and Twilio
 * runbooks still land somewhere sensible now that the feed lives under
 * Settings → Channel monitor.
 */
export default function ChannelMonitor() {
  return (
    <>
      <TopBar crumbs={['Settings', 'Channel Monitor']} />
      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        <ChannelMonitorFeed />
      </div>
    </>
  );
}
